import {baseApi} from './base'
import {DEFAULT_PAGE_SIZE} from '@shared/constants/preferences/pagination'
import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {
  IngestLagSummaryInterface,
  SignalDataGapInterface,
  SignalDataGapStatsInterface,
  SignalTriggerLiveStateInterface,
  SignalValuesResponseInterface,
} from '@interfaces/signal'

const signalsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Paginated data-gap history for one signal (AB#5519). The history is
    // unbounded, so the modal pages through it here rather than reading an inline
    // list off the signal payload (which carries only the 24h count badge).
    getSignalDataGaps: builder.query<
      BaseFetchResultsInterface<SignalDataGapInterface>,
      {signalId: string; page?: number; pageSize?: number}
    >({
      query: ({signalId, page = 0, pageSize = DEFAULT_PAGE_SIZE}) => ({
        url: 'signal-data-gaps/',
        params: {signal: signalId, limit: pageSize, offset: page * pageSize},
      }),
    }),
    // Aggregate reliability stats for one signal (AB#5519): per-window availability
    // + counts + trend, longest gap, and a 30-day daily series for the sparkline.
    getSignalDataGapStats: builder.query<SignalDataGapStatsInterface, {signalId: string}>({
      query: ({signalId}) => ({
        url: 'signal-data-gaps/stats/',
        params: {signal: signalId},
      }),
    }),
    // Tenant-wide ingest-lag summary for the signals toolbar indicator (AB#5519).
    getIngestLagSummary: builder.query<IngestLagSummaryInterface, void>({
      query: () => ({url: 'signals/ingest_lag/'}),
    }),
    // Latest value + timestamp read straight from the TSDB (as_of=now), so the
    // details modal shows the true last reading rather than the cached
    // Signal.last_value fields. Starting point for the signals/values endpoint.
    getSignalLatestValues: builder.query<SignalValuesResponseInterface, {signalIds: string[]}>({
      query: ({signalIds}) => ({
        url: 'signals/values/',
        method: 'POST',
        body: {representation: 'as_of', signal_ids: signalIds},
      }),
    }),
    // Whether each trigger is true on its signal's latest value (AB#6690): the
    // Live Values widget's applicability. Unknown/deleted ids are left out.
    evaluateSignalTriggers: builder.query<
      {triggers: SignalTriggerLiveStateInterface[]},
      {triggerIds: string[]}
    >({
      query: ({triggerIds}) => ({
        url: 'signaltriggers/evaluate/',
        method: 'POST',
        body: {trigger_ids: triggerIds},
      }),
    }),
    exportSignalExcel: builder.mutation<
      Blob,
      {machine?: string; machine_sub_system?: string; timezone_offset_minutes?: number}
    >({
      query: ({machine, machine_sub_system, timezone_offset_minutes}) => {
        const params: Record<string, string> = {}
        if (machine) {
          params.machine = machine
        }
        if (machine_sub_system) {
          params.machine_sub_system = machine_sub_system
        }
        if (timezone_offset_minutes !== undefined) {
          params.timezone_offset_minutes = timezone_offset_minutes.toString()
        }

        return {
          url: 'signals/export-excel-signals/',
          method: 'GET',
          headers: {Accept: '*/*'},
          params,
          responseHandler: (response: Response): Promise<Blob> => response.blob(),
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useExportSignalExcelMutation,
  useGetSignalDataGapsQuery,
  useGetSignalDataGapStatsQuery,
  useGetIngestLagSummaryQuery,
  useGetSignalLatestValuesQuery,
  useLazyGetSignalLatestValuesQuery,
  useEvaluateSignalTriggersQuery,
} = signalsAPI
